import { useCallback, useEffect, useMemo, useState } from "react";
import styles from "./SocialMediaAssessmentViewer.module.css";
import {
  getActiveSocialWindow,
  getSocialWindowList,
} from "./helpers/socialMediaWindows";

const EMPTY = "—";

const safeNum = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const fmt = {
  int: (value) => {
    const num = safeNum(value);
    return num !== null
      ? new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(num)
      : EMPTY;
  },
  dec: (value, digits = 1) => {
    const num = safeNum(value);
    return num !== null ? num.toFixed(digits) : EMPTY;
  },
  pct: (value, digits = 1) => {
    const num = safeNum(value);
    return num !== null ? `${(num * 100).toFixed(digits)}%` : EMPTY;
  },
  date: (value) => {
    if (!value) return EMPTY;
    const date = new Date(value);
    return Number.isNaN(date.getTime())
      ? EMPTY
      : date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  },
};

const getToneClass = (status = "") => {
  const normalized = String(status || "").toLowerCase();
  if (normalized.includes("healthy") || normalized.includes("above")) return styles.good;
  if (normalized.includes("needs") || normalized.includes("behind") || normalized.includes("diverging")) {
    return styles.warn;
  }
  return styles.neutral;
};

const getTextToneClass = (status = "") => {
  const normalized = String(status || "").toLowerCase();
  if (normalized.includes("healthy") || normalized.includes("above")) return styles.textGood;
  if (normalized.includes("needs") || normalized.includes("behind") || normalized.includes("diverging")) {
    return styles.textWarn;
  }
  return styles.textNeutral;
};

const widthForRate = (value) => {
  const num = safeNum(value);
  if (num === null) return "0%";
  return `${Math.max(10, Math.min(100, num * 1200))}%`;
};

const PlatformIcon = ({ platform }) =>
  platform === "instagram" ? (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
    </svg>
  ) : (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005.8 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z" />
    </svg>
  );

function PostCard({ title, post, negative = false }) {
  return (
    <div className={`${styles.effectCard} ${negative ? styles.effectCardNegative : ""}`}>
      <div className={styles.effectHdr}>{title}</div>
      {post ? (
        <>
          <div className={styles.effectText}>{post.caption || post.title || "Untitled post"}</div>
          <div className={styles.effectMeta}>
            {safeNum(post.views) !== null ? <span>{fmt.int(post.views)} views</span> : null}
            {safeNum(post.likes) !== null ? <span>{fmt.int(post.likes)} likes</span> : null}
            {safeNum(post.comments) !== null ? <span>{fmt.int(post.comments)} comments</span> : null}
            {safeNum(post.engagement_rate) !== null ? (
              <span className={styles.effectAccent}>{fmt.pct(post.engagement_rate, 2)}</span>
            ) : null}
          </div>
        </>
      ) : (
        <div className={styles.emptyState}>No post available in this window.</div>
      )}
    </div>
  );
}

function ListCard({ title, subtitle = null, items = [], negative = false }) {
  return (
    <div className={`${styles.patternCard} ${negative ? styles.negative : styles.positive}`}>
      <div className={styles.patternHdr}>{title}</div>
      {subtitle ? <div className={styles.patternSub}>{subtitle}</div> : null}
      <ul className={styles.patternList}>
        {items.length ? items.map((item) => <li key={item} className={styles.patternItem}>{item}</li>) : <li className={styles.patternItem}>No pattern available yet.</li>}
      </ul>
    </div>
  );
}

export default function SocialMediaAssessmentViewer({ data = {}, report = {}, onWindowChange }) {
  const defaultWindowKey = data?.default_window_key || null;
  const initialWindowList = useMemo(
    () => getSocialWindowList({ ...data, available_windows: [] }),
    [data],
  );
  const [activeWindowKey, setActiveWindowKey] = useState(
    defaultWindowKey || initialWindowList[0]?.key || null
  );
  const emitWindowChange = useCallback((nextWindowKey) => {
    if (!nextWindowKey || !onWindowChange) return;
    const entry = initialWindowList.find((w) => String(w.key) === String(nextWindowKey));
    const activeEntry = getActiveSocialWindow(data, nextWindowKey);
    const resolvedEntry = entry || activeEntry || {};
    onWindowChange({
      key: nextWindowKey,
      label: resolvedEntry?.label || entry?.label || nextWindowKey,
      days: resolvedEntry?.days || entry?.days || null,
      startDate:
        resolvedEntry?.startDate ||
        resolvedEntry?.timeframe?.window?.startDate ||
        entry?.startDate ||
        null,
      endDate:
        resolvedEntry?.endDate ||
        resolvedEntry?.timeframe?.window?.endDate ||
        entry?.endDate ||
        null,
      reportId: report?._id || report?.id || null,
      generationKey: data?.generation_key || null,
      reportKey: "socialMedia",
      collection: "social_media_report",
      collectionName: "social_media_report",
      section: "assessment",
      windowKey: nextWindowKey,
      assessmentKey: "social",
      assessmentLabel: "Social Media Assessment",
      type: "assessment_window",
    });
  }, [data, data?.generation_key, initialWindowList, onWindowChange, report?._id, report?.id]);

  useEffect(() => {
    if (!initialWindowList.length) {
      setActiveWindowKey(null);
      return;
    }
    setActiveWindowKey((currentKey) => {
      const stillExists = initialWindowList.some((entry) => entry.key === currentKey);
      if (stillExists) return currentKey;
      return defaultWindowKey || initialWindowList[0]?.key || null;
    });
  }, [defaultWindowKey, initialWindowList]);

  useEffect(() => {
    emitWindowChange(activeWindowKey);
  }, [activeWindowKey, emitWindowChange]);

  const activeWindowData = useMemo(
    () => getActiveSocialWindow(data, activeWindowKey),
    [data, activeWindowKey]
  );

  const content =
    activeWindowData?.social_media_assessment_viewer_content ||
    data?.social_media_assessment_viewer_content ||
    null;

  console.log("[SocialMediaAssessmentViewer] render", {
    reportId: report?._id || report?.id || null,
    collectionName: data?.collection_name || null,
    hasViewerContent: Boolean(content),
    activeWindowKey,
    availableWindowKeys: initialWindowList.map((windowEntry) => windowEntry?.key || null),
  });

  if (!content) {
    return <div className={styles.viewer}><div className={styles.emptyState}>No social media assessment is available yet.</div></div>;
  }

  const timeframe = content?.timeframe || {};
  const summary = content?.assessment_summary || {};
  const growthPulse = content?.growth_and_reach_pulse || {};
  const themes = Array.isArray(content?.content_themes?.themes) ? content.content_themes.themes : [];
  const whenToPost = content?.when_to_post || {};
  const captionPatterns = content?.caption_and_hook_patterns || {};
  const hashtagPatterns = content?.hashtag_patterns || {};
  const brand = content?.brand_and_audience_alignment || {};
  const competitive = content?.competitive_context || {};
  const platformItems = Array.isArray(content?.platform_specific_callouts?.items)
    ? content.platform_specific_callouts.items
    : [];

  const competitiveCards = [
    competitive?.posting_frequency_gap ? { label: "Posting frequency gap", ...competitive.posting_frequency_gap } : null,
    competitive?.engagement_rate_position ? { label: "Engagement rate position", ...competitive.engagement_rate_position } : null,
    competitive?.content_format_mix ? { label: "Content format mix", ...competitive.content_format_mix } : null,
  ].filter(Boolean);

  return (
    <div className={styles.viewer}>
      <div className={styles.tfBar}>
        {initialWindowList.length ? (
          <select
            className={styles.windowPickerSelect}
            value={activeWindowKey || ""}
            onChange={(e) => {
              const nextWindowKey = e.target.value;
              setActiveWindowKey(nextWindowKey);
              emitWindowChange(nextWindowKey);
            }}
            aria-label="Social media assessment time window"
          >
            {initialWindowList.map((windowEntry) => (
              <option key={windowEntry.key} value={windowEntry.key}>
                {windowEntry.label}
              </option>
            ))}
          </select>
        ) : null}
        {timeframe?.why ? <div className={styles.tfWhy}>{timeframe.why}</div> : null}
      </div>

      <div className={`${styles.asmSummary} ${styles.asmSummaryWarning}`}>
        <div className={`${styles.asmStatus} ${styles.asmStatusWarning}`} />
        <div>
          <div className={`${styles.asmSummaryLabel} ${styles.asmSummaryLabelWarning}`}>{summary?.title || "Assessment summary"}</div>
          <div className={styles.asmSummaryText}>
            {summary?.text || EMPTY}
            {summary?.prompt ? ` ${summary.prompt}` : ""}
          </div>
        </div>
      </div>

      <section className={styles.section}>
        <div className={styles.sectionHdr}>
          <div className={styles.sectionTitle}>1. Growth and reach pulse</div>
          <div className={styles.sectionSub}>Per platform</div>
        </div>
        <div className={styles.platformGrid}>
          {(growthPulse?.platforms || []).map((platform) => (
            <div key={platform.platform} className={styles.platformCard}>
              <div className={styles.platformHdr}>
                <div className={styles.platformLogo}><PlatformIcon platform={String(platform.platform || "").toLowerCase()} /></div>
                <div className={styles.platformName}>{platform.platform || EMPTY}</div>
                <div className={`${styles.platformStatus} ${getToneClass(platform.status)}`}>{platform.status || EMPTY}</div>
              </div>
              <div className={styles.platformMetrics}>
                <div className={styles.pm}>
                  <div className={styles.pmLabel}>Followers</div>
                  <div className={styles.pmValue}>{fmt.int(platform.followers)}</div>
                  <div className={`${styles.pmChange} ${styles.pmUp}`}>
                    {safeNum(platform.follower_delta_period) !== null ? fmt.pct(platform.follower_delta_period) : "Growth unavailable"}
                  </div>
                </div>
                <div className={styles.pm}>
                  <div className={styles.pmLabel}>Cadence</div>
                  <div className={styles.pmValue}>{fmt.dec(platform.cadence_posts_per_week)}</div>
                  <div className={`${styles.pmChange} ${styles.pmAmber}`}>{platform.cadence_note || "posts/week"}</div>
                </div>
                <div className={styles.pm}>
                  <div className={styles.pmLabel}>Eng rate</div>
                  <div className={styles.pmValue}>{fmt.pct(platform.engagement_rate)}</div>
                  <div className={`${styles.pmChange} ${getTextToneClass(platform.engagement_note)}`}>{platform.engagement_note || EMPTY}</div>
                </div>
                <div className={styles.pm}>
                  <div className={styles.pmLabel}>{platform.average_distribution_label || "Distribution"}</div>
                  <div className={styles.pmValue}>{fmt.int(platform.average_distribution_value)}</div>
                  <div className={styles.pmChange}>{platform.average_distribution_note || EMPTY}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
        {growthPulse?.summary ? <div className={styles.growthNote}>{growthPulse.summary}</div> : null}
      </section>

      <section className={styles.section}>
        <div className={styles.sectionHdr}>
          <div className={styles.sectionTitle}>2. Content effectiveness</div>
          <div className={styles.sectionSub}>What&apos;s working, what&apos;s not</div>
        </div>
        <div className={styles.effectivenessPanel}>{content?.content_effectiveness?.summary || EMPTY}</div>
        <div className={styles.effectGrid}>
          <PostCard title="Strongest post" post={content?.content_effectiveness?.best_post} />
          <PostCard title="Weakest post" post={content?.content_effectiveness?.worst_post} negative />
        </div>
        {content?.content_effectiveness?.recommendation ? (
          <div className={styles.recommendationNote}>{content.content_effectiveness.recommendation}</div>
        ) : null}
      </section>

      <section className={styles.section}>
        <div className={styles.sectionHdr}>
          <div className={styles.sectionTitle}>3. What&apos;s working: content themes</div>
          <div className={styles.sectionSub}>Themes ranked by average engagement rate</div>
        </div>
        <div className={styles.themeList}>
          {themes.length ? themes.map((theme, index) => (
            <div key={theme.name} className={styles.themeRow}>
              <div className={`${styles.themeRank} ${index === 0 ? styles.themeRankTop : ""}`}>{theme.rank || index + 1}</div>
              <div className={styles.themeInfo}>
                <div className={styles.themeName}>{theme.name || theme.theme || EMPTY}</div>
                <div className={styles.themeMeta}>{fmt.int(theme.post_count)} posts · {fmt.int(theme.avg_views)} avg views</div>
              </div>
              <div className={styles.themeBarWrap}>
                <div className={styles.themeBar}>
                  <div className={styles.themeBarFill} style={{ width: widthForRate(theme.avg_engagement_rate) }} />
                </div>
              </div>
              <div className={styles.themeRate}>{fmt.pct(theme.avg_engagement_rate)}</div>
            </div>
          )) : <div className={styles.emptyPadded}>No theme performance data available yet.</div>}
        </div>
      </section>

      <section className={styles.section}>
        <div className={styles.sectionHdr}>
          <div className={styles.sectionTitle}>4. When to post</div>
          <div className={styles.sectionSub}>Based on engagement across your post history</div>
        </div>
        <div className={styles.timingGrid}>
          <div className={styles.timingCard}>
            <div className={styles.timingCardLabel}>Best day to post</div>
            <div className={styles.timingCardVal}>{whenToPost?.best_day?.label || EMPTY}</div>
            <div className={styles.timingCardNote}>{whenToPost?.best_day?.note || EMPTY}</div>
          </div>
          <div className={styles.timingCard}>
            <div className={styles.timingCardLabel}>Best time to post</div>
            <div className={styles.timingCardVal}>{whenToPost?.best_time?.label || EMPTY}</div>
            <div className={styles.timingCardNote}>{whenToPost?.best_time?.note || EMPTY}</div>
          </div>
        </div>
        <div className={styles.timingWorst}>
          <div className={styles.timingWorstLabel}>Avoid</div>
          <div>{whenToPost?.avoid?.label || EMPTY}. {whenToPost?.avoid?.note || ""}</div>
        </div>
      </section>

      <section className={styles.section}>
        <div className={styles.sectionHdr}>
          <div className={styles.sectionTitle}>5. Caption and hook patterns</div>
          <div className={styles.sectionSub}>What your top posts do differently</div>
        </div>
        <div className={styles.patternGrid}>
          <ListCard
            title="Patterns in high-performing captions"
            subtitle={captionPatterns?.avg_caption_length_top != null ? `Avg ${captionPatterns.avg_caption_length_top} words · top 5 posts` : null}
            items={captionPatterns?.high_performer_caption_patterns || captionPatterns?.top_patterns || []}
          />
          <ListCard
            title="Patterns in low-performing captions"
            subtitle={captionPatterns?.avg_caption_length_bottom != null ? `Avg ${captionPatterns.avg_caption_length_bottom} words · bottom 5 posts` : null}
            items={captionPatterns?.low_performer_caption_patterns || captionPatterns?.low_patterns || []}
            negative
          />
        </div>
      </section>

      <section className={styles.section}>
        <div className={styles.sectionHdr}>
          <div className={styles.sectionTitle}>6. Hashtag patterns</div>
          <div className={styles.sectionSub}>What hashtags your best and worst posts use</div>
        </div>
        <div className={`${styles.hashtagGroup} ${styles.top}`}>
          <div className={styles.hashtagGroupLabel}>Hashtags on top posts</div>
          <div className={styles.hashtagPills}>
            {(hashtagPatterns?.top_post_hashtags || []).map((tag) => <span key={tag} className={styles.hashPill}>{tag}</span>)}
          </div>
        </div>
        <div className={`${styles.hashtagGroup} ${styles.bottom}`}>
          <div className={styles.hashtagGroupLabel}>Hashtags on low-performing posts</div>
          <div className={styles.hashtagPills}>
            {(hashtagPatterns?.bottom_post_hashtags || hashtagPatterns?.low_post_hashtags || []).map((tag) => <span key={tag} className={`${styles.hashPill} ${styles.hashPillMuted}`}>{tag}</span>)}
          </div>
        </div>
        {(hashtagPatterns?.hashtag_pattern_insight || hashtagPatterns?.summary)
          ? <div className={styles.hashtagSummary}>{hashtagPatterns.hashtag_pattern_insight || hashtagPatterns.summary}</div>
          : null}
      </section>

      <section className={styles.section}>
        <div className={styles.sectionHdr}>
          <div className={styles.sectionTitle}>7. Brand and audience alignment</div>
          <div className={styles.sectionSub}>Is content consistent with your brand and audience?</div>
        </div>
        <div className={styles.alignGrid}>
          <div className={styles.alignCard}><div className={styles.alignLabel}>Brand voice consistency</div><div className={styles.alignValue}>{fmt.int(brand.brand_voice_consistency)}%</div></div>
          <div className={styles.alignCard}><div className={styles.alignLabel}>Audience relevance</div><div className={styles.alignValue}>{fmt.int(brand.audience_relevance)}%</div></div>
          <div className={styles.alignCard}><div className={styles.alignLabel}>Premium positioning</div><div className={styles.alignValue}>{fmt.int(brand.premium_positioning)}%</div></div>
          <div className={styles.alignCard}><div className={styles.alignLabel}>Visual consistency</div><div className={styles.alignValue}>{fmt.int(brand.visual_consistency)}%</div></div>
        </div>
        {brand?.summary ? <div className={styles.alignSummary}>{brand.summary}</div> : null}
      </section>

      <section className={styles.section}>
        <div className={styles.sectionHdr}>
          <div className={styles.sectionTitle}>8. Competitive context</div>
          <div className={styles.sectionSub}>Benchmarked against working category benchmarks</div>
        </div>
        <div className={styles.competitiveGrid}>
          {competitiveCards.map((item) => (
            <div key={item.label} className={styles.competitiveCard}>
              <div className={styles.competitiveLabel}>{item.label}</div>
              <div className={`${styles.competitiveStatus} ${getToneClass(item.status)}`}>{item.status || EMPTY}</div>
              <div className={styles.competitiveText}>{item.summary || EMPTY}</div>
            </div>
          ))}
        </div>
        {competitive?.note ? <div className={styles.competitiveNote}>{competitive.note}</div> : null}
      </section>

      <section className={styles.section}>
        <div className={styles.sectionHdr}>
          <div className={styles.sectionTitle}>9. Platform-specific callouts</div>
          <div className={styles.sectionSub}>What matters differently per platform</div>
        </div>
        <div className={styles.calloutGrid}>
          {platformItems.map((item) => (
            <div key={item.platform} className={styles.calloutCard}>
              <div className={styles.calloutTitle}>{item.platform || EMPTY}</div>
              <div className={styles.calloutText}>{item.summary || EMPTY}</div>
              <div className={styles.calloutRecommendation}>{item.recommendation || EMPTY}</div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
